import { useEffect, useState } from "react";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { BoardContentsState } from "@/types/creative-library";

export function useBoardContents(token: string | null, refresh: number) {
  const [state, setState] = useState<BoardContentsState>({
    token: null,
    board: null,
    items: [],
    status: "ready",
  });
  useEffect(() => {
    if (!token) {
      setState({ token: null, board: null, items: [], status: "ready" });
      return;
    }
    let active = true;
    // A refresh of the same Board stays on screen; only another Board starts empty.
    setState((current) =>
      current.token === token && current.status === "ready"
        ? { ...current, refreshFailed: false }
        : { token, board: null, items: [], status: "loading" },
    );
    void Promise.all([
      creativeLibraryApi.board(token),
      creativeLibraryApi.creatives(undefined, token),
    ])
      .then(([board, items]) => {
        if (active) setState({ token, board, items, status: "ready" });
      })
      .catch((error) => {
        if (!active) return;
        const isGone = [400, 403, 404].includes(error?.response?.status);
        setState((current) =>
          !isGone && current.token === token && current.status === "ready"
            ? { ...current, refreshFailed: true }
            : {
                token,
                board: null,
                items: [],
                status: isGone ? "unavailable" : "error",
              },
        );
      });
    return () => {
      active = false;
    };
  }, [token, refresh]);
  // A route change never paints the prior Board's assets while its effect starts.
  return state.token === token
    ? state
    : { token, board: null, items: [], status: "loading" as const };
}
